import { useContext, useEffect } from "react";
import { useFetcher } from "@remix-run/react";

import type { OrgSites, Site } from "~/entities/siteEntities";
import { OrderSource } from "~/entities/orderEntities";
import OrderContext from "~/contexts/OrderContext";
import { urlGen } from "~/helpers/routesHelpers";
import {
  resolveCateringEntrySite,
  siteHasCatering,
} from "~/helpers/cateringHelpers";

export interface ExperienceEntry {
  show: boolean;
  href: string;
  label: string;
  description: string;
}

/**
 * Drives the experience-switch entry in the top nav + hamburger.
 *
 * It's a two-way switch keyed off the current experience:
 *  - In **online** → offers **Catering** (the shareable `/catering` front door, or
 *    `/catering/<site>` when the current store caters).
 *  - In **catering** → flips to **Order food** (everyday ordering) so there's a
 *    clear way back, staying on the same store.
 *
 * The menus don't load the org-sites list, so this lazily fetches `/api/orgSites`
 * (the same resource route the StoreFinder uses) the first time the menu opens —
 * no cost on pages where the menu stays shut. `show`:
 *  - always true in catering (always offer the way back), else
 *  - only where the org/current site actually offers catering.
 */
export const useCateringEntry = (
  site: Site | undefined,
  /** Only fetch once the menu has been opened, to keep idle pages cheap. */
  active: boolean,
): ExperienceEntry => {
  const fetcher = useFetcher<OrgSites>();
  const { orderSource } = useContext(OrderContext);

  useEffect(() => {
    if (active && fetcher.state === "idle" && fetcher.data === undefined) {
      fetcher.load("/api/orgSites");
    }
  }, [active, fetcher]);

  const inCatering = orderSource === OrderSource.CATERING;
  const currentSiteHasCatering = siteHasCatering(site?.orderTypes);
  const cateringEntrySite = resolveCateringEntrySite(fetcher.data?.sites);

  const show =
    inCatering || currentSiteHasCatering || cateringEntrySite !== null;

  // In catering → the entry flips to everyday ordering on the same store.
  if (inCatering) {
    return {
      show,
      href: urlGen("Order", {
        orderSource: OrderSource.ONLINE,
        siteId: site?.id ?? null,
      }),
      label: "Order food",
      description: "Everyday ordering",
    };
  }

  // In online → the entry points at catering. Stay in the current store when it
  // caters; otherwise the shareable `/catering` front door resolves to one.
  return {
    show,
    href:
      currentSiteHasCatering && site
        ? urlGen("Order", { orderSource: OrderSource.CATERING, siteId: site.id })
        : urlGen("Order", { orderSource: OrderSource.CATERING, siteId: null }),
    label: "Catering",
    description: "Order ahead for events",
  };
};
